import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Button, Chip, Paper, Typography, LinearProgress } from '@mui/material';
import { MapPinIcon, PhoneIcon, TruckIcon, XMarkIcon, ClockIcon, UserIcon, ExclamationTriangleIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { websocketService } from '../../services/emergencyWebsocket';
import { orderAPI } from '../../services/marketplaceAPI';
import { initFeatherIcons } from '../../utils/api';
import PatientTrackingMap from '../../components/maps/PatientTrackingMap';
import PaymentGateway from '../../components/payments/PaymentGateway';
import muiTheme from '../../theme/muiTheme';
import '../../styles/EmergencyDashboard.css';
import '../../styles/maps.css';

const AMBULANCE_CATEGORIES = {
  Basic: {
    name: 'Basic Ambulance',
    description: 'Basic life support ambulance',
    price: 500,
    estimatedTime: '15-20 mins'
  },
  Premium: {
    name: 'Premium Ambulance',
    description: 'Premium ambulance with advanced care',
    price: 2000,
    estimatedTime: '10-15 mins'
  },
  ICU: {
    name: 'ICU Ambulance',
    description: 'Fully equipped ICU ambulance',
    price: 5000,
    estimatedTime: '5-10 mins'
  },
  NICU: {
    name: 'NICU Ambulance',
    description: 'Neonatal intensive care unit ambulance',
    price: 6000,
    estimatedTime: '5-10 mins'
  }
};

const REQUEST_STATUS = {
  pending: 'pending',
  accepted: 'accepted',
  started_journey: 'started_journey',
  on_the_way: 'on_the_way',
  almost_there: 'almost_there',
  looking_for_patient: 'looking_for_patient',
  received_patient: 'received_patient',
  dropping_off: 'dropping_off',
  completed: 'completed',
  cancelled: 'cancelled'
};

const STATUS_LABELS = {
  [REQUEST_STATUS.pending]: 'Searching for Driver',
  [REQUEST_STATUS.accepted]: 'Driver Found',
  [REQUEST_STATUS.started_journey]: 'Driver Started Journey',
  [REQUEST_STATUS.on_the_way]: 'Driver on the Way',
  [REQUEST_STATUS.almost_there]: 'Driver Almost There',
  [REQUEST_STATUS.looking_for_patient]: 'Driver Looking for You',
  [REQUEST_STATUS.received_patient]: 'Driver Received You',
  [REQUEST_STATUS.dropping_off]: 'Dropping Off',
  [REQUEST_STATUS.completed]: 'Journey Completed',
  [REQUEST_STATUS.cancelled]: 'Request Cancelled'
};

const STATUS_DESCRIPTIONS = {
  [REQUEST_STATUS.pending]: 'We are searching for a driver to assist you',
  [REQUEST_STATUS.accepted]: 'A driver has accepted your request and is on their way',
  [REQUEST_STATUS.started_journey]: 'Your driver has started their journey to your location',
  [REQUEST_STATUS.on_the_way]: 'Your driver is on their way to your location',
  [REQUEST_STATUS.almost_there]: 'Your driver is approaching your location',
  [REQUEST_STATUS.looking_for_patient]: 'Your driver has arrived and is looking for you',
  [REQUEST_STATUS.received_patient]: 'You have been picked up by the driver',
  [REQUEST_STATUS.dropping_off]: 'You are being taken to your destination',
  [REQUEST_STATUS.completed]: 'Your journey has been completed',
  [REQUEST_STATUS.cancelled]: 'Your request has been cancelled'
};

const STATUS_FLOW = [
  { status: REQUEST_STATUS.pending, label: 'Searching for Driver' },
  { status: REQUEST_STATUS.accepted, label: 'Driver Found' },
  { status: REQUEST_STATUS.started_journey, label: 'Driver Started Journey' },
  { status: REQUEST_STATUS.on_the_way, label: 'Driver on the Way' },
  { status: REQUEST_STATUS.almost_there, label: 'Driver Almost There' },
  { status: REQUEST_STATUS.looking_for_patient, label: 'Driver Looking for You' },
  { status: REQUEST_STATUS.received_patient, label: 'Driver Received You' },
  { status: REQUEST_STATUS.dropping_off, label: 'Dropping Off' },
  { status: REQUEST_STATUS.completed, label: 'Journey Completed' },
  { status: REQUEST_STATUS.cancelled, label: 'Request Cancelled' }
];

const EmergencyPatientDashboard = () => {
  const navigate = useNavigate();
  const [requestData, setRequestData] = useState({
    location: '',
    description: '',
    emergencyType: 'cardiac',
    ambulanceCategory: 'Basic'
  });
  const [activeRequest, setActiveRequest] = useState(null);
  const [canRequest, setCanRequest] = useState(true);
  const [cancelTimeout, setCancelTimeout] = useState(null);
  const [user, setUser] = useState(null);
  const [showStatusGuide, setShowStatusGuide] = useState(true);
  const [estimatedArrivalTime, setEstimatedArrivalTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [driverLocation, setDriverLocation] = useState(null);
  const [patientLocation, setPatientLocation] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);
  const [requestStatus, setRequestStatus] = useState(null);
  const [isRequestSent, setIsRequestSent] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [rideCompleted, setRideCompleted] = useState(false);
  const [rideDetails, setRideDetails] = useState(null);

  useEffect(() => {
    // Initialize feather icons
    initFeatherIcons();
    const timer = setTimeout(() => initFeatherIcons(), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Load user data from localStorage
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData || {
      id: 'anonymous',
      name: 'Anonymous',
      phone: 'N/A',
      age: 'N/A'
    });

    // Load existing emergency payments
    const existingPayments = JSON.parse(localStorage.getItem('emergencyPayments') || '[]');
    const userPayments = existingPayments.filter(payment => 
      payment.patientId === (userData?._id || userData?.id || 'anonymous')
    );
    
    // If there are completed payments, show them
    if (userPayments.length > 0) {
      console.log('Found existing emergency payments:', userPayments);
    }

    websocketService.connect();

    const handleRequestStatusUpdate = (message) => {
      const data = message.data || message;
      if (data) {
        const { status, driver, timestamp, requestId } = data;
        
        // Only process if this is for our active request
        if (activeRequest && (requestId === activeRequest.id || requestId === activeRequest.data?.id)) {
          // Update request status
          setRequestStatus(status);
          
          // Update driver info if available
          if (driver) {
            setDriverInfo(driver);
            
            // Request location sharing permission when driver accepts
            if (status === 'accepted') {
              setTimeout(() => {
                console.log('Requesting location permission for real-time tracking');
                websocketService.send('location:request-permission', { requestId: requestId || activeRequest.id });
              }, 1000);
            }
          }

          // Update active request with new status and timestamp
          setActiveRequest(prev => {
            const updated = {
              ...prev,
              status,
              driverInfo: driver || prev.driverInfo,
              lastUpdate: timestamp || new Date().toISOString()
            };
            return updated;
          });

          // Update estimated arrival time based on status
          switch (status) {
            case REQUEST_STATUS.accepted:
              setEstimatedArrivalTime(new Date(Date.now() + 15 * 60000)); // 15 minutes
              break;
            case REQUEST_STATUS.started_journey:
              setEstimatedArrivalTime(new Date(Date.now() + 12 * 60000)); // 12 minutes
              break;
            case REQUEST_STATUS.on_the_way:
              setEstimatedArrivalTime(new Date(Date.now() + 10 * 60000)); // 10 minutes
              break;
            case REQUEST_STATUS.almost_there:
              setEstimatedArrivalTime(new Date(Date.now() + 5 * 60000)); // 5 minutes
              break;
            case REQUEST_STATUS.looking_for_patient:
              setEstimatedArrivalTime(new Date(Date.now() + 2 * 60000)); // 2 minutes
              break;
            case REQUEST_STATUS.received_patient:
              setEstimatedArrivalTime(null); // Clear ETA as patient is picked up
              break;
            case REQUEST_STATUS.dropping_off:
              setEstimatedArrivalTime(new Date(Date.now() + 10 * 60000)); // 10 minutes to destination
              break;
            case REQUEST_STATUS.completed:
              // Ride completed - prepare for payment
              setRideCompleted(true);
              
              // Calculate ride details for payment
              const rideStartTime = activeRequest?.timestamp || new Date().toISOString();
              const rideEndTime = new Date().toISOString();
              const rideDuration = Math.round((new Date(rideEndTime) - new Date(rideStartTime)) / 60000); // minutes
              
              // Mock distance calculation (in production, this would come from GPS tracking)
              const mockDistance = Math.floor(Math.random() * 20 + 5); // 5-25 km
              
              setRideDetails({
                startTime: rideStartTime,
                endTime: rideEndTime,
                duration: rideDuration,
                distance: mockDistance,
                ambulanceType: requestData.ambulanceCategory,
                emergencyType: requestData.emergencyType
              });
              
              // Calculate initial payment amount
              const categoryPrice = AMBULANCE_CATEGORIES[requestData.ambulanceCategory]?.price || 500;
              const distanceCharge = Math.floor(mockDistance * 50); // 50 BDT per km
              const timeCharge = rideDuration > 30 ? 200 : 0; // Additional 200 BDT for rides > 30 min
              const initialAmount = categoryPrice + distanceCharge + timeCharge;
              
              setPaymentAmount(initialAmount);
              
              // Show payment modal
              setShowPaymentModal(true);
              
              // Don't clear active request yet - wait for payment
              break;
            case REQUEST_STATUS.cancelled:
              setActiveRequest(null);
              setDriverInfo(null);
              setRequestStatus(null);
              setEstimatedArrivalTime(null);
              setRideCompleted(false);
              setRideDetails(null);
              break;
            default:
              break;
          }
        }
      }
    };

    const handleDriverStatusUpdate = (message) => {
      const data = message.data || message;
      if (data) {
        const { driver, status, timestamp, requestId } = data;
        
        // Only process if this is for our active request
        if (activeRequest && (requestId === activeRequest.id || requestId === activeRequest.data?.id)) {
          // Update driver info with new status
          if (driver) {
            setDriverInfo(driver);
          }

          // Update active request with new driver status
          setActiveRequest(prev => {
            const updated = {
              ...prev,
              status: status || prev.status,
              driverInfo: driver || prev.driverInfo,
              lastUpdate: timestamp || new Date().toISOString()
            };
            return updated;
          });

          // Update request status if provided
          if (status) {
            setRequestStatus(status);
          }
        }
      }
    };

    // Handle cancellation responses
    const handleCancelResponse = (data) => {
      console.log('Cancel request response:', data);
      if (data.success) {
        setActiveRequest(null);
        setEstimatedArrivalTime(null);
        setRequestStatus(null);
        setDriverInfo(null);
        
        // Clear the cancel timeout
        if (cancelTimeout) {
          clearTimeout(cancelTimeout);
          setCancelTimeout(null);
        }
        
        alert('Request cancelled successfully');
      }
    };
    
    const handleCancelBlocked = (data) => {
      console.log('Cancel request blocked:', data);
      alert(data.message || 'Cannot cancel request - driver is almost there or has already arrived');
    };
    
    // Subscribe to WebSocket events
    websocketService.subscribe('request_status_update', handleRequestStatusUpdate);
    websocketService.subscribe('driver_status_update', handleDriverStatusUpdate);
    websocketService.subscribe('accept_request', handleRequestStatusUpdate); // Also handle accept_request messages
    websocketService.subscribe('request_cancelled', handleCancelResponse);
    websocketService.subscribe('cancel_request_blocked', handleCancelBlocked);

    // Subscribe to location updates from driver
    const handleLocationReceived = (data) => {
      console.log('Received location update in patient dashboard:', data);
      if (activeRequest?.id === data.requestId && data.userType === 'driver') {
        const locationData = {
          latitude: data.location.latitude || data.location.lat,
          longitude: data.location.longitude || data.location.lng,
          accuracy: data.location.accuracy,
          timestamp: data.timestamp,
          heading: data.location.heading,
          speed: data.location.speed
        };
        console.log('Normalized driver location for patient:', locationData);
        setDriverLocation(locationData);
        
        // Also update the active request with driver location
        setActiveRequest(prev => ({
          ...prev,
          driverLocation: locationData
        }));
      }
    };

    // Subscribe to location permission events
    const handleLocationPermissionGranted = (data) => {
      console.log('Location permission granted in patient dashboard:', data);
      if (activeRequest?.id === data.requestId) {
        console.log('Location sharing is now available for this request');
      }
    };

    // Subscribe to tracking active events
    const handleTrackingActive = (data) => {
      console.log('Location tracking is now active in patient dashboard:', data);
      if (activeRequest?.id === data.requestId) {
        console.log('Real-time tracking is active between driver and patient');
      }
    };

    websocketService.subscribe('location:received', handleLocationReceived);
    websocketService.subscribe('location:permission-granted', handleLocationPermissionGranted);
    websocketService.subscribe('location:tracking-active', handleTrackingActive);

    return () => {
      websocketService.unsubscribe('request_status_update', handleRequestStatusUpdate);
      websocketService.unsubscribe('driver_status_update', handleDriverStatusUpdate);
      websocketService.unsubscribe('accept_request', handleRequestStatusUpdate);
      websocketService.unsubscribe('location:received', handleLocationReceived);
      websocketService.unsubscribe('location:permission-granted', handleLocationPermissionGranted);
      websocketService.unsubscribe('location:tracking-active', handleTrackingActive);
    };
  }, [activeRequest?.id, activeRequest, requestData.ambulanceCategory, requestData.emergencyType]); // Include all dependencies

  // const resetRequest = () => {
  //   setIsRequestSent(false);
  //   setActiveRequest(null);
  //   setDriverInfo(null);
  //   setRequestStatus(null);
  //   setCanRequest(true);
  //   if (cancelTimeout) {
  //     clearTimeout(cancelTimeout);
  //     setCancelTimeout(null);
  //   }
  // };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRequestData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlaceRequest = async (e) => {
    e.preventDefault();
    if (!canRequest) return;

    setIsRequestSent(true);
    setCanRequest(false);

    const newRequest = {
      id: crypto.randomUUID(),
      ...requestData,
      timestamp: new Date().toISOString(),
      patientId: user?._id || user?.id || 'anonymous',
      patientInfo: {
        patientId: user?._id || user?.id || 'anonymous',
        name: user?.name || 'Anonymous',
        phone: user?.phone || 'N/A',
        age: user?.age || 'N/A'
      },
      status: 'pending'
    };

    try {
      // Ensure authenticated before sending the request
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        const uId = u?._id || u?.id;
        const uType = (u?.role || 'patient').toString().toLowerCase();
        if (websocketService.socket?.connected && uId) {
          websocketService.authenticate(uId, uType);
        }
      } catch (_) {}
      websocketService.send('new_request', newRequest);
      setActiveRequest(newRequest);
    } catch (error) {
      setIsRequestSent(false);
      setCanRequest(true);
    }

    const timeout = setTimeout(() => {
      setCanRequest(true);
    }, 5 * 60 * 1000);
    setCancelTimeout(timeout);
  };

  const handleCancelRequest = async () => {
    if (!activeRequest) return;
    
    // Check if cancellation is allowed based on current status
    const currentStatus = requestStatus || activeRequest.status;
    const nonCancellableStatuses = [
      'almost_there',
      'looking_for_patient',
      'received_patient',
      'dropping_off',
      'completed'
    ];
    
    if (nonCancellableStatuses.includes(currentStatus)) {
      alert('Cannot cancel request - driver is almost there or has already arrived. Please contact support if needed.');
      return;
    }
    
    // Confirm cancellation
    if (!window.confirm('Are you sure you want to cancel this request?')) {
      return;
    }
    
    setIsLoading(true);
    try {
      const cancelData = {
        requestId: activeRequest.id,
        status: 'cancelled',
        timestamp: new Date().toISOString()
      };
      websocketService.send('cancel_request', cancelData);
      
      // Wait for confirmation before clearing state
      // State will be cleared on successful cancellation response
    } catch (error) {
      console.error('Error cancelling request:', error);
      alert('Failed to cancel request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async () => {
    console.log('Payment button clicked!');
    console.log('Current rideDetails:', rideDetails);
    console.log('Current requestData:', requestData);
    
    // Calculate payment amount based on ambulance category and ride details
    const categoryPrice = AMBULANCE_CATEGORIES[requestData.ambulanceCategory]?.price || 1000;
    
    // Calculate additional charges based on ride details
    let additionalCharges = 0;
    if (rideDetails) {
      // Add distance-based charges (mock calculation)
      const distance = rideDetails.distance || 5; // Default 5km
      additionalCharges = Math.floor(distance * 50); // 50 BDT per km
      
      // Add time-based charges for long rides
      if (rideDetails.duration > 30) { // More than 30 minutes
        additionalCharges += 200; // Additional 200 BDT
      }
    }
    
    const calculatedAmount = categoryPrice + additionalCharges;
    console.log('Calculated payment amount:', calculatedAmount);
    
    setPaymentAmount(calculatedAmount);
    // Open the payment gateway - same as marketplace
    setShowPaymentModal(true);
    
    console.log('Opening payment gateway');
  };

  const handlePaymentSuccess = async (paymentData) => {
    try {
      setIsLoading(true);
      
      console.log('Payment successful for emergency ambulance:', paymentData);
      
      // Close the payment gateway immediately (same as marketplace)
      setShowPaymentModal(false);
      
      // Validate required data
      if (!paymentData.transactionId && !paymentData.orderId) {
        alert('Payment transaction ID is missing. Please contact support.');
        return;
      }
      
      // Format shipping address from ride details (required by backend)
      const locationAddress = typeof activeRequest.location === 'string' 
        ? activeRequest.location 
        : activeRequest.location?.address || 'Emergency Location';
      
      // Split address into components (basic parsing - can be improved)
      const addressParts = locationAddress.split(',').map(part => part.trim());
      const shippingAddress = {
        street: addressParts[0] || locationAddress,
        city: addressParts[1] || 'Dhaka',
        state: addressParts[2] || 'Dhaka',
        zipCode: addressParts[3] || '1000',
        country: 'Bangladesh'
      };
      
      // Use the EXACT same approach as marketplace - format payload like CheckoutPage
      const payload = {
        shippingAddress: shippingAddress,
        notes: `Emergency ambulance service - ${requestData.ambulanceCategory}. Request ID: ${activeRequest.id}`,
        loyaltyPointsUsed: 0,
        couponApplied: null,
        insuranceData: null,
        payment: {
          transactionId: paymentData.transactionId || paymentData.orderId,
          paymentMethod: paymentData.paymentMethod,
          details: paymentData.paymentDetails || {}
        },
        paymentType: 'emergency_ambulance', // Explicitly set payment type for backend
        requestId: activeRequest.id, // Pass requestId directly for backend to update EmergencyRequest
        amount: paymentAmount,
        originalAmount: paymentAmount
      };
      
      console.log('Creating order with payload (marketplace format):', payload);
      
      // Create order after payment - same as marketplace
      const response = await orderAPI.createOrderAfterPayment(payload);
      console.log('Order creation response:', response);
      console.log('Order creation response data:', response?.data);
      console.log('Order creation response success:', response?.success);
      
      if (response && response.success) {
        // Update the request status to reflect payment completion
        setActiveRequest(prev => ({
          ...prev,
          paymentStatus: 'completed',
          paymentDetails: {
            ...paymentData,
            transactionId: payload.payment.transactionId
          },
          orderId: response.data.orderId
        }));

        // Show success message
        alert('Payment completed successfully! Your emergency service has been paid for.');
        
        // Clear all ride-related state and reset for new request
        setActiveRequest(null);
        setDriverInfo(null);
        setRequestStatus(null);
        setEstimatedArrivalTime(null);
        setRideCompleted(false);
        setRideDetails(null);
        setPatientLocation(null);
        setDriverLocation(null);
        setCanRequest(true);
        setIsRequestSent(false);
        
        // Refresh the page to reset everything
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        throw new Error(response.message || 'Failed to create order');
      }
      
    } catch (error) {
      console.error('Error creating order:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      alert(`Payment was processed but order creation failed: ${errorMessage}. Please contact support.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    // Payment gateway handles its own close/cancel logic
  };

  const getStatusProgress = (status) => {
    const statusIndex = STATUS_FLOW.findIndex(step => step.status === status);
    if (statusIndex === -1) return 0;
    return ((statusIndex + 1) / STATUS_FLOW.length) * 100;
  };

  // If there's an active request, show the tracking dashboard
  if (activeRequest) {
    return (
      <ThemeProvider theme={muiTheme}>
      <div className="emergency-dashboard" style={{ minHeight: '100vh', height: 'auto', overflowY: 'auto', overflowX: 'hidden' }}>
        <div className="dashboard-container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '1rem', width: '100%' }}>
          {/* Modern Header Section */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              color: '#FFFFFF',
              boxShadow: '0 10px 30px rgba(30, 64, 175, 0.2)'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: '#FFFFFF',
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <TruckIcon style={{ width: 24, height: 24 }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                  Emergency Service
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard')}
                sx={{ 
                  textTransform: 'none', 
                  fontWeight: 600,
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: '#FFFFFF',
                  '&:hover': {
                    borderColor: '#FFFFFF',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Go to Dashboard
              </Button>
            </Box>
          </Paper>

          {/* Main Content Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
            {/* Left Column - Request Status & Info */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Modern Active Request Card */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  borderRadius: 3,
                  background: '#FFFFFF',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(30, 64, 175, 0.1)'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B', mb: 1 }}>
                      Active Request
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      {STATUS_DESCRIPTIONS[activeRequest.status]}
                    </Typography>
                  </Box>
                  <Chip
                    label={STATUS_LABELS[activeRequest.status] || activeRequest.status}
                    color={
                      activeRequest.status === 'pending' ? 'warning' :
                      activeRequest.status === 'accepted' ? 'primary' :
                      activeRequest.status === 'completed' ? 'success' : 'default'
                    }
                    sx={{ 
                      fontWeight: 700, 
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      height: '32px'
                    }}
                  />
                </Box>

                {/* Modern Progress Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E293B' }}>
                      Journey Progress
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E40AF' }}>
                      {Math.round(getStatusProgress(activeRequest.status))}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getStatusProgress(activeRequest.status)}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: '#E2E8F0',
                      mb: 3,
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                        borderRadius: 5,
                        boxShadow: '0 2px 8px rgba(30, 64, 175, 0.3)'
                      }
                    }}
                  />
                  
                  {/* Modern Progress Steps */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
                      gap: 2,
                      mt: 3
                    }}
                  >
                    {STATUS_FLOW.slice(0, 5).map((step, index) => {
                      const isCompleted = STATUS_FLOW.findIndex(s => s.status === activeRequest.status) >= index;
                      const isCurrent = STATUS_FLOW.findIndex(s => s.status === activeRequest.status) === index;
                      return (
                        <Box
                          key={step.status}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            p: 2,
                            borderRadius: 2,
                            background: isCompleted 
                              ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                              : '#F8FAFC',
                            border: isCurrent ? '2px solid #1E40AF' : '1px solid #E2E8F0',
                            textAlign: 'center',
                            transition: 'all 0.3s ease',
                            transform: isCurrent ? 'scale(1.05)' : 'scale(1)',
                            boxShadow: isCompleted ? '0 4px 12px rgba(30, 64, 175, 0.2)' : 'none'
                          }}
                        >
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              bgcolor: isCompleted ? '#FFFFFF' : '#CBD5E1',
                              mb: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: isCompleted ? 'none' : '2px solid #94A3B8'
                            }}
                          >
                            {isCompleted && (
                              <Typography sx={{ color: '#1E40AF', fontSize: '10px', fontWeight: 700 }}>✓</Typography>
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: isCompleted ? 700 : 500,
                              color: isCompleted ? '#FFFFFF' : '#64748B',
                              fontSize: '0.7rem',
                              lineHeight: 1.2
                            }}
                          >
                            {step.label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Consolidated Request Information */}
                <Box 
                  sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%)',
                    border: '1px solid #E2E8F0',
                    mb: 3
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 2.5 }}>
                    Request Details
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', display: 'block', mb: 0.5 }}>
                        Detailed Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E293B' }}>
                        {typeof activeRequest.location === 'string' 
                          ? activeRequest.location 
                          : activeRequest.location?.address || `${activeRequest.location?.latitude?.toFixed(6)}, ${activeRequest.location?.longitude?.toFixed(6)}` || 'Location not available'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', display: 'block', mb: 0.5 }}>
                        Emergency Type
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E293B', textTransform: 'capitalize' }}>
                        {activeRequest.emergencyType}
                      </Typography>
                    </Box>
                    <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', display: 'block', mb: 0.5 }}>
                        Description
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#1E293B' }}>
                        {activeRequest.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Driver Information Card */}
                {activeRequest.driverInfo && (
                  <Box 
                    sx={{ 
                      p: 3, 
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                      color: '#FFFFFF !important',
                      mb: 3,
                      boxShadow: '0 4px 20px rgba(30, 64, 175, 0.2)',
                      '& *': {
                        color: 'inherit'
                      }
                    }}
                  >
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#FFFFFF !important', 
                        mb: 2.5, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        '& svg': {
                          color: '#FFFFFF !important'
                        }
                      }}
                    >
                      <UserIcon style={{ width: 20, height: 20, color: '#FFFFFF' }} />
                      Driver Information
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                      <Box>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600, 
                            color: 'rgba(255, 255, 255, 0.9) !important', 
                            display: 'block', 
                            mb: 0.5 
                          }}
                        >
                          Name
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 600, 
                            color: '#FFFFFF !important' 
                          }}
                        >
                          {activeRequest.driverInfo.name}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600, 
                            color: 'rgba(255, 255, 255, 0.9) !important', 
                            display: 'block', 
                            mb: 0.5 
                          }}
                        >
                          Mobile
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 600, 
                            color: '#FFFFFF !important' 
                          }}
                        >
                          {activeRequest.driverInfo.phone}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600, 
                            color: 'rgba(255, 255, 255, 0.9) !important', 
                            display: 'block', 
                            mb: 0.5 
                          }}
                        >
                          Vehicle Number
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 600, 
                            color: '#FFFFFF !important' 
                          }}
                        >
                          {activeRequest.driverInfo.carRegistration || activeRequest.driverInfo.vehicleNumber || 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600, 
                            color: 'rgba(255, 255, 255, 0.9) !important', 
                            display: 'block', 
                            mb: 0.5 
                          }}
                        >
                          Ambulance Type
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 600, 
                            color: '#FFFFFF !important', 
                            textTransform: 'capitalize' 
                          }}
                        >
                          {activeRequest.driverInfo.ambulanceType || activeRequest.driverInfo.vehicleType || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Status Messages */}
                {rideCompleted && activeRequest.status === 'completed' && (
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      borderRadius: 2,
                      p: 3,
                      mb: 3,
                      color: '#FFFFFF',
                      boxShadow: '0 4px 20px rgba(16, 185, 129, 0.2)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TruckIcon style={{ width: 28, height: 28 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#FFFFFF', mb: 0.5 }}>
                          Ride Completed!
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                          Your emergency service has been completed. Please proceed to payment.
                        </Typography>
                        {rideDetails && (
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', mt: 1 }}>
                            Duration: {rideDetails.duration} min • Distance: {rideDetails.distance} km
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                {activeRequest.paymentStatus === 'completed' && (
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      borderRadius: 2,
                      p: 3,
                      mb: 3,
                      color: '#FFFFFF',
                      boxShadow: '0 4px 20px rgba(16, 185, 129, 0.2)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CreditCardIcon style={{ width: 28, height: 28 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#FFFFFF', mb: 0.5 }}>
                          Payment Completed
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                          Thank you! Your emergency service payment has been processed successfully.
                        </Typography>
                        {activeRequest.paymentDetails && (
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', mt: 1 }}>
                            Amount: ৳{activeRequest.paymentDetails.amount} • Method: {activeRequest.paymentDetails.paymentMethod}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {rideCompleted && activeRequest.status === 'completed' && (
                      <Button
                        variant="contained"
                        onClick={handlePayment}
                        disabled={isLoading}
                        startIcon={<CreditCardIcon style={{ width: 20, height: 20 }} />}
                        sx={{ 
                          textTransform: 'none', 
                          fontWeight: 700,
                          px: 4,
                          py: 1.5,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                            boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)'
                          }
                        }}
                      >
                        Pay for Service (৳{paymentAmount || 'Calculating...'})
                      </Button>
                    )}
                    {activeRequest.status !== 'completed' && 
                     activeRequest.status !== 'cancelled' && (() => {
                      const currentStatus = requestStatus || activeRequest.status;
                      const nonCancellableStatuses = [
                        'almost_there',
                        'looking_for_patient',
                        'received_patient',
                        'dropping_off'
                      ];
                      const canCancel = !nonCancellableStatuses.includes(currentStatus);
                      
                      return (
                        <Button
                          variant="contained"
                          color="error"
                          onClick={handleCancelRequest}
                          disabled={isLoading || !canCancel}
                          startIcon={<XMarkIcon style={{ width: 20, height: 20 }} />}
                          sx={{ 
                            textTransform: 'none', 
                            fontWeight: 700,
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                            '&:hover': {
                              boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)'
                            }
                          }}
                          title={!canCancel ? 'Cannot cancel - driver is almost there or has already arrived' : 'Cancel this request'}
                        >
                          Cancel Request
                        </Button>
                      );
                    })()}
                  </Box>
                </Box>
              </Paper>

              {/* Modern Map Tracking Card */}
              {activeRequest.driverInfo && (
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 0, 
                    borderRadius: 3,
                    background: '#FFFFFF',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(30, 64, 175, 0.1)',
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ p: 3, pb: 2, borderBottom: '1px solid #E2E8F0' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                      Live Tracking
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <PatientTrackingMap
                      requestId={activeRequest.id}
                      driverLocation={driverLocation || activeRequest.driverLocation}
                      driverInfo={activeRequest.driverInfo}
                      initialPatientLocation={activeRequest.initialRequestedLocation || activeRequest.location}
                      onLocationUpdate={(location) => {
                        console.log('Patient location updated:', location);
                        setPatientLocation(location);
                      }}
                      height="500px"
                      className="w-full"
                    />
                  </Box>
                </Paper>
              )}
            </Box>
            
            {/* Right Column - Quick Info */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Modern Quick Info Card */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                  color: '#FFFFFF',
                  boxShadow: '0 4px 20px rgba(30, 64, 175, 0.2)'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF', mb: 3 }}>
                  Quick Info
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        p: 1.5,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <ClockIcon style={{ width: 20, height: 20, color: '#FFFFFF' }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', mb: 0.5 }}>
                        Estimated Arrival
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                        {estimatedArrivalTime 
                          ? `${Math.round((estimatedArrivalTime - new Date()) / 60000)} min`
                          : 'Calculating...'}
                      </Typography>
                    </Box>
                  </Box>
                  {activeRequest.driverInfo && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <Box
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          p: 1.5,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)'
                        }}
                      >
                        <PhoneIcon style={{ width: 20, height: 20, color: '#FFFFFF' }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', mb: 0.5 }}>
                          Driver
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                          {activeRequest.driverInfo.name}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        </div>
      </div>
      
      {/* Payment Gateway Component - Same as Marketplace */}
      {activeRequest && (
        <PaymentGateway
          open={showPaymentModal}
          onClose={handlePaymentCancel}
          amount={paymentAmount}
          paymentType="emergency_ambulance"
          onSuccess={handlePaymentSuccess}
          userProfile={user}
          orderId={`EMG-AMB-${activeRequest?.id || Date.now()}`}
        />
      )}
      </ThemeProvider>
    );
  }

  // Show the form for new requests
  return (
    <div className="emergency-dashboard">
      <div className="dashboard-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="dashboard-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <motion.h1 
              className="dashboard-title"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              Emergency Service
            </motion.h1>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </button>
          </div>

          <AnimatePresence>
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handlePlaceRequest}
              className="form-container"
            >
              <div className="form-row">
                <div className="form-col">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <label className="form-label">Detailed Address</label>
                <input
                  type="text"
                  value={requestData.location}
                  onChange={handleInputChange}
                  name="location"
                      className="form-input"
                      placeholder="Enter your detailed address (street, area, city)"
                  required
                />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <label className="form-label">Ambulance Type</label>
                <select
                  value={requestData.ambulanceCategory}
                  onChange={handleInputChange}
                  name="ambulanceCategory"
                      className="form-select"
                  required
                >
                      <option value="Basic">Basic (500 BDT)</option>
                      <option value="Premium">Premium (2000 BDT)</option>
                      <option value="ICU">ICU (5000 BDT)</option>
                      <option value="NICU">NICU (6000 BDT)</option>
                </select>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    <label className="form-label">Emergency Type</label>
                <select
                  value={requestData.emergencyType}
                  onChange={handleInputChange}
                  name="emergencyType"
                      className="form-select"
                  required
                >
                      <option value="cardiac">Cardiac</option>
                      <option value="trauma">Trauma</option>
                      <option value="stroke">Stroke</option>
                      <option value="other">Other</option>
                </select>
                  </motion.div>
              </div>

                <div className="form-col">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <label className="form-label">Description</label>
                <textarea
                  value={requestData.description}
                  onChange={handleInputChange}
                  name="description"
                      className="form-textarea"
                      placeholder="Describe your emergency"
                    required
                  />
            </motion.div>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="btn btn-primary w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <PhoneIcon className="h-5 w-5 mr-2" />
                    Request Emergency Service
                  </span>
                )}
              </motion.button>
        </motion.form>
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Payment Gateway Component - Same as Marketplace */}
      {activeRequest && (
        <PaymentGateway
          open={showPaymentModal}
          onClose={handlePaymentCancel}
          amount={paymentAmount}
          paymentType="emergency_ambulance"
          onSuccess={handlePaymentSuccess}
          userProfile={user}
          orderId={`EMG-AMB-${activeRequest?.id || Date.now()}`}
        />
      )}
    </div>
  );
};

export default EmergencyPatientDashboard; 
